import React, { useState, useCallback } from "react";
import { Box, Typography, TextField } from "@mui/material";
import liff from "../../../lib/liff";
import PotionLayout from "../components/PotionLayout";
import Loading from "default/components/Loading";
import StyledButton from "default/components/StyledButton";
import ButtonDialog from "default/components/ButtonDialog";
import { useNavigate } from "react-router-dom";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";
import { usePotionAssets } from "../hooks/usePotionAssets";
import { payPotionTamil } from "utils/api";

const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

function Payment() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { soulId, lng } = useUrlParams();
  const { tamil, potions, isLoading, isError } = usePotionAssets(soulId);

  const [amount, setAmount] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paidAmount, setPaidAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentTamil, setCurrentTamil] = useState(null);

  const displayTamil = currentTamil !== null ? currentTamil : tamil;
  const numAmount = parseInt(amount, 10) || 0;
  const exceedsBalance = numAmount > displayTamil;
  const exceedsMax = numAmount > 9999;
  const isValid = numAmount > 0 && !exceedsBalance && !exceedsMax;

  const handleAmountChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setAmount(value);
    setErrorMessage("");
  }, []);

  const handlePay = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    try {
      await payPotionTamil(soulId, numAmount);
    } catch (error) {
      if (error.status === 422) {
        setErrorMessage("タミルが不足しています");
      } else {
        setErrorMessage("エラーが発生しました。もう一度お試しください");
      }
      return;
    }
    try {
      const imageUrl = `https://soulchat.sekaie.in/assets/potion/content_images/kaede/tamil/${numAmount}.webp`;
      await liff.sendMessages([
        {
          type: "image",
          originalContentUrl: imageUrl,
          previewImageUrl: imageUrl,
        },
      ]);
      liff.closeWindow();
    } catch (error) {
      console.warn("sendMessages failed after successful tamil payment", error);
      setCurrentTamil((prev) => (prev !== null ? prev : tamil) - numAmount);
      setPaidAmount(numAmount);
      setPaid(true);
    }
  }, [numAmount, soulId, tamil]);

  if (isLoading) {
    return (
      <PotionLayout width="100%">
        <Loading />
      </PotionLayout>
    );
  }

  if (isError) {
    return (
      <PotionLayout width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 6,
            gap: 3,
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            P<span style={{ color: "#8CB808" }}>a</span>yment
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "20px",
              fontWeight: 500,
              color: theme.system.colors.text.primary,
              textAlign: "center",
            }}
          >
            データの取得に失敗しました
          </Typography>
        </Box>
      </PotionLayout>
    );
  }

  if (paid) {
    return (
      <PotionLayout width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 6,
            gap: 3,
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            P<span style={{ color: "#8CB808" }}>a</span>yment
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "20px",
              fontWeight: 500,
              color: theme.system.colors.text.primary,
              textAlign: "center",
            }}
          >
            <Box sx={{ mt: 9 }} />
            {paidAmount} タミルを支払いました
          </Typography>
          <StyledButton
            variant="contained"
            onClick={() => liff.closeWindow()}
            sx={(theme) => ({
              mt: 9,
              width: "200px",
              ...theme.components.button,
            })}
          >
            チャットに戻る
          </StyledButton>
        </Box>
      </PotionLayout>
    );
  }

  return (
    <PotionLayout width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 6,
          gap: 3,
          px: 3,
        }}
      >
        {/* Title */}
        <Typography
          sx={(theme) => ({
            ...theme.components.sectionHeader,
          })}
        >
          P<span style={{ color: "#8CB808" }}>a</span>yment
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontFamily: theme.system.fonts.body,
            fontSize: "20px",
            fontWeight: 500,
            color: theme.system.colors.text.primary,
            textAlign: "center",
            WebkitTextStroke: "3px #FFFFFF",
            paintOrder: "stroke fill",
          }}
        >
          いくら支払いますか？
        </Typography>

        {/* Tamil Input */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            gap: 1,
            mt: 2,
          }}
        >
          <TextField
            value={amount}
            onChange={handleAmountChange}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              style: {
                fontFamily: theme.system.fonts.heading,
                fontSize: "36px",
                fontWeight: 500,
                color: theme.system.colors.text.primary,
                textAlign: "center",
                width: "120px",
                padding: "4px 0",
              },
            }}
            variant="standard"
            sx={{
              "& .MuiInput-underline:before": {
                borderBottomColor: theme.system.colors.text.primary,
                borderBottomWidth: "2px",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: theme.system.colors.text.primary,
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: theme.system.colors.text.primary,
              },
            }}
            placeholder="0"
          />
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "24px",
              fontWeight: 500,
              color: theme.system.colors.text.primary,
              WebkitTextStroke: "3px #FFFFFF",
              paintOrder: "stroke fill",
            }}
          >
            タミル
          </Typography>
        </Box>

        {/* Balance Display */}
        <Typography
          sx={{
            fontFamily: theme.system.fonts.body,
            fontSize: "16px",
            fontWeight: 500,
            color: theme.system.colors.text.primary,
            mt: 1,
            WebkitTextStroke: "2px #FFFFFF",
            paintOrder: "stroke fill",
          }}
        >
          手持ち：{isError ? "--" : displayTamil} タミル
        </Typography>

        {/* Validation Error */}
        {(exceedsBalance && amount !== "") || (exceedsMax && amount !== "") || errorMessage ? (
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "14px",
              fontWeight: 500,
              color: theme.system.colors.accent.red,
            }}
          >
            {errorMessage || (exceedsMax ? "9,999タミルまで支払えます" : "タミルが不足しています")}
          </Typography>
        ) : null}

        {/* 1. 支払う */}
        <StyledButton
          variant="contained"
          onClick={handlePay}
          disabled={!isValid}
          sx={(theme) => ({
            mt: 2,
            width: "200px",
            ...theme.components.button,
            opacity: isValid ? 1 : 0.5,
          })}
        >
          支払う
        </StyledButton>

        {/* 2. タミル交換所 */}
        <StyledButton
          variant="contained"
          onClick={() => navigate(withLng(`/potion/exchange_money?soul_id=${soulId}`, lng))}
          sx={(theme) => ({
            mt: 2,
            width: "200px",
            ...theme.components.button,
          })}
        >
          タミル交換所
        </StyledButton>
      </Box>

      {/* Confirmation Dialog */}
      <ButtonDialog
        open={confirmOpen}
        title={`${numAmount} タミルを支払いますか？`}
        buttons={[
          {
            label: "支払う",
            onClick: handleConfirm,
          },
        ]}
        handleClose={() => setConfirmOpen(false)}
      />
    </PotionLayout>
  );
}

export default Payment;
